'use client';

import React, { useMemo } from 'react';
import { WidgetConfigProvider } from './WidgetConfigProvider';
import type { ProfileConfigWidget } from 'src/api/redux/profileConfiguration/types';
import type { PlayItemWidget } from 'src/api/redux/profileConfiguration/types';
import type { WidgetVariant } from './types';

/** Convert a PlayItemWidget to a ProfileConfigWidget shape for WidgetRenderer */
export const toProfileWidget = (pw: PlayItemWidget): ProfileConfigWidget => ({
  id: pw.display_order ?? 0,
  code: pw.code,
  label: pw.label ?? null,
  description: pw.description ?? null,
  content_key: pw.content_key ?? null,
  code_set: pw.code_set,
  code_sets: pw.code_sets ?? null,
  filter_set: pw.filter_set ?? null,
  min_year: pw.min_year ?? null,
  max_year: pw.max_year ?? null,
  comparison_code_set: pw.comparison_code_set ?? null,
  encounter: pw.encounter ?? null,
  comparison_encounter: pw.comparison_encounter ?? null,
  numerator_code_set: pw.numerator_code_set ?? null,
  numerator_encounter: pw.numerator_encounter ?? null,
  denominator_code_set: pw.denominator_code_set ?? null,
  denominator_encounter: pw.denominator_encounter ?? null,
  minuend_code_set: pw.minuend_code_set ?? null,
  minuend_encounter: pw.minuend_encounter ?? null,
  subtrahend_code_set: pw.subtrahend_code_set ?? null,
  subtrahend_encounter: pw.subtrahend_encounter ?? null,
  entity: pw.entity ?? null,
  target_strategy: null,
  engage_strategy: pw.engage_strategy ?? null,
  fresh_faces_strategy: pw.fresh_faces_strategy ?? null,
  look_back_months: pw.look_back_months ?? null,
  direction: pw.direction ?? null,
  display_order: pw.display_order,
  display_mode: pw.display_mode ?? null,
  benchmark: pw.benchmark ?? null,
  benchmark_operator: pw.benchmark_operator ?? null,
  volume_type: pw.volume_type ?? null,
  numerator_volume_type: pw.numerator_volume_type ?? null,
  denominator_volume_type: pw.denominator_volume_type ?? null,
  display_format: pw.display_format ?? null,
  year: pw.year ?? null,
  average_sale_price: pw.average_sale_price ?? null,
  capture_rate: pw.capture_rate ?? null,
  volume_source: pw.volume_source ?? null,
  network_rollup: pw.network_rollup ?? null,
  show_percent_change: pw.show_percent_change ?? null,
  encounters: pw.encounters ?? null,
  evidence: pw.evidence,
});

/**
 * Widget component resolver — injected by widgetRegistry to avoid
 * circular dependency (widgetRegistry → widgets → PlaysSection → here).
 *
 * widgetRegistry registers a factory via registerResolverInit at module
 * load (safe — just stores a reference). WidgetRenderer calls it lazily on
 * first render, when all modules are fully initialised and TDZ is not a risk.
 */
let _resolveWidget: ((code: string, variant?: WidgetVariant) => React.ComponentType | undefined) | null =
  null;

let _initResolver: (() => void) | null = null;

export const setWidgetResolver = (
  resolver: (code: string, variant?: WidgetVariant) => React.ComponentType | undefined
) => {
  _resolveWidget = resolver;
};

export const registerResolverInit = (init: () => void) => {
  _initResolver = init;
};

export const WidgetRenderer: React.FC<{
  widget: ProfileConfigWidget;
  variant?: WidgetVariant;
  componentOverrides?: Record<string, React.ComponentType>;
  parentLabel?: string;
  parentSummary?: string;
}> = ({ widget, variant = 'default', componentOverrides, parentLabel, parentSummary }) => {
  if (!_resolveWidget && _initResolver) {
    _initResolver();
  }

  const WidgetComponent = useMemo(
    () => componentOverrides?.[widget.code] ?? _resolveWidget?.(widget.code, variant),
    [componentOverrides, widget.code, variant]
  );

  if (!WidgetComponent) return null;

  return (
    <WidgetConfigProvider widget={widget} parentLabel={parentLabel} parentSummary={parentSummary}>
      {/* eslint-disable-next-line react-hooks/static-components -- dynamic widget resolved at runtime from a registry */}
      <WidgetComponent />
    </WidgetConfigProvider>
  );
};
